import {GfaLocaleServiceV1} from '@volkswagen-onehub/gfa-locale-service';
import {DataVersionServiceV1} from '@volkswagen-onehub/data-version-service';
import {getFeatureAppConfigurationEndpoint} from '@volkswagen-onehub/vw-e-mobility-service-responses';
import {
  BffNaturalLanguageConfigurationResponse,
  NonSimulatorFeatureAppName,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {
  getCustomConfigsFromLocalStorage,
  readMockIds,
} from './fetch-bff-config';
import {encodeBffConfigParameters} from './encode-bff-config-parameters';
import {ServiceConfigEndpointProvider} from '@volkswagen-onehub/service-config-endpoint-provider/lib/v1';

export async function fetchNaturalLanguageBffConfig(
  featureServices: {
    localeService: GfaLocaleServiceV1;
    dataVersion?: DataVersionServiceV1;
  },
  serviceConfigEndpointProvider: ServiceConfigEndpointProvider,
  envBaseUrl?: string
): Promise<BffNaturalLanguageConfigurationResponse> {
  const {language, countryCode} = featureServices.localeService;

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const mockIds = readMockIds(urlParams);

  const debugParamForBff = urlParams.get('debug') || undefined;

  /**
   * Workaround to allow cache busting from within the CMS.
   * We use the hash/version of the current CMS service config, so TQA/ROM can
   * circumvent the cache by changing/republishing the service config.
   * This mirrors the MOFA implementation, but isn't that pretty.
   *
   * (One issue is that TQA won't allow any url parameters or similar for this,
   * as it then "could be different from the production behaviour", oh well)
   */
  const cacheBuster = featureServices.dataVersion?.serviceConfigsVersion;

  const searchString = encodeBffConfigParameters({
    language,
    countryCode,
    serviceConfigEndpointProvider,
    mockIds,
    cacheBuster,
    debug: debugParamForBff,
  });

  const featureAppName = NonSimulatorFeatureAppName.NaturalLanguage;
  const customConfigBody = getCustomConfigsFromLocalStorage(featureAppName);

  // If we have any custom configs we use the POST endpoint
  const endpoint = getFeatureAppConfigurationEndpoint(
    customConfigBody,
    featureAppName
  );

  // This is a human-configured value, get rid of ending slash if it was provided.
  const baseUrl = (envBaseUrl ?? '').replace(/\/$/, '');

  const requestUrl = `${baseUrl}${endpoint}?${searchString}`;

  const rawResponse = await fetch(
    requestUrl,
    customConfigBody ? {method: 'POST', body: customConfigBody} : undefined
  );

  if (!rawResponse.ok) {
    throw new Error(`${rawResponse.status} - ${rawResponse.statusText}`);
  }

  return rawResponse.json();
}
